import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Badge,
  Link,
} from '@chakra-ui/react';
import { supabase } from '../lib/supabase';

export default function Signup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orgSlug = searchParams.get('org');

  const [orgName, setOrgName] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validating, setValidating] = useState(!!orgSlug);

  useEffect(() => {
    if (!orgSlug) return;
    setValidating(true);
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    fetch(`${API_BASE}/api/billing/validate-org?slug=${encodeURIComponent(orgSlug)}`)
      .then(r => {
        if (!r.ok) throw new Error('Invalid organization');
        return r.json();
      })
      .then(data => {
        setOrgName(data.org_name);
        setOrgId(data.org_id);
      })
      .catch(() => {
        setError('Invalid organization link.');
      })
      .finally(() => setValidating(false));
  }, [orgSlug]);

  const handleSignup = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: authErr } = await supabase.auth.signUp({ email, password });
      if (authErr) throw authErr;

      if (data.user && orgId) {
        const { error: memberErr } = await supabase
          .from('org_members')
          .insert({ org_id: orgId, user_id: data.user.id, role: 'editor' });
        if (memberErr) {
          console.warn('Auto-link to org failed:', memberErr.message);
        }
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (!orgSlug) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Box bg="white" p={8} borderRadius="xl" boxShadow="md" maxW="400px" w="full" textAlign="center">
          <Heading size="md" mb={4}>Sign Up</Heading>
          <Text color="gray.600" mb={6}>
            Registration requires an invitation link from your organization.
          </Text>
          <Text color="gray.500" fontSize="sm">
            Contact us to get started:{' '}
            <Link href="mailto:support@mesagroupconsulting.com" color="brand.600" fontWeight="600">
              support@mesagroupconsulting.com
            </Link>
          </Text>
          <Button mt={6} variant="ghost" size="sm" onClick={() => navigate('/login')}>
            Already have an account? Sign in
          </Button>
        </Box>
      </Box>
    );
  }

  if (validating) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Text color="gray.500">Validating organization...</Text>
      </Box>
    );
  }

  if (success) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
        <Box bg="white" p={8} borderRadius="xl" boxShadow="md" maxW="400px" w="full" textAlign="center">
          <Heading size="md" mb={4} color="green.600">Account Created!</Heading>
          <Text color="gray.600" mb={4}>
            Check your email to verify your account, then sign in.
          </Text>
          <Button colorScheme="brand" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
      <Box bg="white" p={8} borderRadius="xl" boxShadow="md" maxW="400px" w="full">
        <VStack spacing={5}>
          <Heading size="md">Create Your Account</Heading>
          {orgName && (
            <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
              Joining: {orgName}
            </Badge>
          )}

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
            />
          </FormControl>

          <Button
            colorScheme="brand"
            w="full"
            onClick={handleSignup}
            isLoading={loading}
            isDisabled={!email || password.length < 6}
          >
            Sign Up
          </Button>

          <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
            Already have an account? Sign in
          </Button>
        </VStack>
      </Box>
    </Box>
  );
}
